"use client";

import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Plus, Settings, X } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	createTableColumn,
	createTableRow,
	isTableColumnType,
	TABLE_COLUMN_TYPES,
	TABLE_MAX_COLUMNS,
	TABLE_MAX_ROWS_LIMIT,
} from "@/lib/fields/field-config";
import type { FormField, TableRowDef } from "@/lib/database";
import { ConditionalVisibilitySettings } from "../conditional-visibility-settings";
import type { FieldSettingsProps } from "./types";

/**
 * Настройки поля «Таблица» (t_82191af7): редактор колонок (тип из белого
 * списка, label, placeholder, required, options для select, ширина px),
 * макс. строк и условная видимость колонок (на уровне строки).
 */
export function TableFieldSettings({
	field,
	onUpdateSettings,
}: FieldSettingsProps) {
	const t = useTranslations("fieldSettings");
	const tf = useTranslations("fields");
	const columns =
		(field.settings?.tableColumns as FormField[] | undefined) || [];
	const maxRows = field.settings?.tableMaxRows ?? 10;
	const fixedRows =
		(field.settings?.tableFixedRows as TableRowDef[] | undefined) || [];

	const [expandedColumns, setExpandedColumns] = useState<{
		[id: string]: boolean;
	}>({});

	const setColumns = (next: FormField[]) => {
		onUpdateSettings({ tableColumns: next });
	};

	const setFixedRows = (next: TableRowDef[]) => {
		onUpdateSettings({ tableFixedRows: next });
	};

	const addFixedRow = () => {
		const idx = fixedRows.length + 1;
		setFixedRows([
			...fixedRows,
			createTableRow(t(`table.defaultRowLabel`, { n: idx })),
		]);
	};

	const removeFixedRow = (rowId: string) => {
		setFixedRows(fixedRows.filter((r) => r.id !== rowId));
	};

	const moveFixedRow = (index: number, dir: -1 | 1) => {
		const target = index + dir;
		if (target < 0 || target >= fixedRows.length) return;
		const next = [...fixedRows];
		[next[index], next[target]] = [next[target], next[index]];
		setFixedRows(next);
	};

	const updateFixedRow = (rowId: string, updates: Partial<TableRowDef>) => {
		setFixedRows(
			fixedRows.map((r) => (r.id === rowId ? { ...r, ...updates } : r)),
		);
	};

	const addColumn = (type: string) => {
		if (columns.length >= TABLE_MAX_COLUMNS) return;
		if (!isTableColumnType(type)) return;
		setColumns([...columns, createTableColumn(type, tf)]);
	};

	const removeColumn = (colId: string) => {
		setColumns(columns.filter((c) => c.id !== colId));
	};

	const moveColumn = (index: number, dir: -1 | 1) => {
		const target = index + dir;
		if (target < 0 || target >= columns.length) return;
		const next = [...columns];
		[next[index], next[target]] = [next[target], next[index]];
		setColumns(next);
	};

	const updateColumn = (colId: string, updates: Partial<FormField>) => {
		setColumns(
			columns.map((c) => (c.id === colId ? { ...c, ...updates } : c)),
		);
	};

	const updateColumnSettings = (
		colId: string,
		updates: Partial<NonNullable<FormField["settings"]>>,
	) => {
		setColumns(
			columns.map((c) =>
				c.id === colId
					? { ...c, settings: { ...c.settings, ...updates } }
					: c,
			),
		);
	};

	const toggleColumnExpansion = (colId: string) => {
		setExpandedColumns((prev) => ({ ...prev, [colId]: !prev[colId] }));
	};

	return (
		<div className="flex flex-col gap-4">
			<Card className="gap-2 p-4 shadow-none">
				<CardHeader className="p-0">
					<CardTitle className="flex items-center gap-2 text-lg">
						{t("table.columnsTitle", { count: columns.length })}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-3 p-0">
					{columns.length >= TABLE_MAX_COLUMNS ? (
						<p className="text-amber-600 text-xs dark:text-amber-400">
							{t("table.maxColumnsReached", { n: TABLE_MAX_COLUMNS })}
						</p>
					) : (
						<div className="flex flex-wrap gap-2">
							{TABLE_COLUMN_TYPES.map((type) => (
								<Button
									aria-label={t("table.addColumnOfType", {
										type: tf(`types.${type}.label`),
									})}
									className="h-8"
									key={type}
									onClick={() => addColumn(type)}
									size="sm"
									type="button"
									variant="outline"
								>
									<Plus aria-hidden="true" className="mr-1 size-3.5" />
									{tf(`columns.${type}`)}
								</Button>
							))}
						</div>
					)}

					{columns.length === 0 ? (
						<div
							aria-live="polite"
							className="rounded-md border border-border border-dashed p-6 text-center text-muted-foreground"
							role="status"
						>
							<p className="text-sm">{t("table.noColumnsYet")}</p>
						</div>
					) : (
						<div className="flex flex-col gap-3">
							{columns.map((col, idx) => {
								const colName =
									col.label || tf(`columns.${col.type}`);
								return (
									<Card
										className="gap-2 p-3 shadow-none"
										key={col.id}
									>
										<div className="flex items-center justify-between gap-2">
											<div className="flex min-w-0 items-center gap-2">
												<span className="truncate font-medium text-card-foreground text-sm">
													{colName}
												</span>
												<span className="shrink-0 rounded bg-muted px-2 py-0.5 text-muted-foreground text-xs">
													{tf(`columns.${col.type}`)}
												</span>
											</div>
											<div className="flex shrink-0 items-center gap-0.5">
												<Button
													aria-label={t("table.moveColumnLeft", {
														name: colName,
													})}
													className="size-7"
													disabled={idx === 0}
													onClick={() => moveColumn(idx, -1)}
													size="icon"
													type="button"
													variant="ghost"
												>
													<ChevronLeft aria-hidden="true" className="size-3.5" />
												</Button>
												<Button
													aria-label={t("table.moveColumnRight", {
														name: colName,
													})}
													className="size-7"
													disabled={idx === columns.length - 1}
													onClick={() => moveColumn(idx, 1)}
													size="icon"
													type="button"
													variant="ghost"
												>
													<ChevronRight aria-hidden="true" className="size-3.5" />
												</Button>
												<Button
													aria-label={t("table.columnSettings", {
														name: colName,
													})}
													className="size-7"
													onClick={() => toggleColumnExpansion(col.id)}
													size="icon"
													type="button"
													variant="ghost"
												>
													<Settings aria-hidden="true" className="size-3.5" />
												</Button>
												<Button
													aria-label={t("table.removeColumn", {
														name: colName,
													})}
													className="size-7 text-muted-foreground hover:text-destructive"
													onClick={() => removeColumn(col.id)}
													size="icon"
													type="button"
													variant="ghost"
												>
													<X aria-hidden="true" className="size-3.5" />
												</Button>
											</div>
										</div>

										{expandedColumns[col.id] && (
											<div className="flex flex-col gap-3 border-border/60 border-t pt-3">
												<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
													<div className="flex flex-col gap-1">
														<Label
															className="font-medium text-sm"
															htmlFor={`${col.id}-label`}
														>
															{t("table.columnLabel")}
														</Label>
														<Input
															autoComplete="off"
															id={`${col.id}-label`}
															onChange={(e) =>
																updateColumn(col.id, {
																	label: e.target.value,
																})
															}
															placeholder={t("table.columnLabelPlaceholder")}
															value={col.label}
														/>
													</div>
													<div className="flex flex-col gap-1">
														<Label
															className="font-medium text-sm"
															htmlFor={`${col.id}-placeholder`}
														>
															{t("table.columnPlaceholder")}
														</Label>
														<Input
															autoComplete="off"
															id={`${col.id}-placeholder`}
															onChange={(e) =>
																updateColumn(col.id, {
																	placeholder: e.target.value,
																})
															}
															placeholder={t("table.columnPlaceholderPlaceholder")}
															value={col.placeholder || ""}
														/>
													</div>
												</div>

												{col.type === "select" && (
													<div className="flex flex-col gap-1">
														<Label
															className="font-medium text-sm"
															htmlFor={`${col.id}-options`}
														>
															{t("table.selectOptions")}
														</Label>
														<Input
															autoComplete="off"
															id={`${col.id}-options`}
															onChange={(e) =>
																updateColumn(col.id, {
																	options: e.target.value
																		.split(",")
																		.map((s) => s.trim())
																		.filter(Boolean),
																})
															}
															placeholder={t("table.selectOptionsPlaceholder")}
															value={(col.options || [])
																.map((o) =>
																	typeof o === "string" ? o : (o?.value ?? ""),
																)
																.join(", ")}
														/>
														<p className="text-muted-foreground text-xs">
															{t("table.selectOptionsHelp")}
														</p>
													</div>
												)}

												<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
													<div className="flex flex-col gap-1">
														<Label
															className="font-medium text-sm"
															htmlFor={`${col.id}-width`}
														>
															{t("table.columnWidth")}
														</Label>
														<Input
															id={`${col.id}-width`}
															inputMode="numeric"
															max={600}
															min={80}
															onChange={(e) => {
																const n = Number.parseInt(e.target.value, 10);
																updateColumnSettings(col.id, {
																	tableColumnWidth: Number.isFinite(n)
																		? Math.min(600, Math.max(80, n))
																		: 200,
																});
															}}
															type="number"
															value={col.settings?.tableColumnWidth ?? 200}
														/>
														<p className="text-muted-foreground text-xs">
															{t("table.columnWidthHelp")}
														</p>
													</div>
													<div className="flex items-center justify-between gap-2 sm:justify-end sm:gap-4">
														<div className="flex flex-col gap-1">
															<Label
																className="font-medium text-sm"
																htmlFor={`${col.id}-required`}
															>
																{t("table.columnRequired")}
															</Label>
															<p className="text-muted-foreground text-xs">
																{t("table.columnRequiredHelp")}
															</p>
														</div>
														<Switch
															checked={col.required}
															id={`${col.id}-required`}
															onCheckedChange={(checked) =>
																updateColumn(col.id, { required: checked })
															}
														/>
													</div>
												</div>

												<ConditionalVisibilitySettings
													field={col}
													onUpdateSettings={(updates) =>
														updateColumnSettings(col.id, {
															...col.settings,
															...updates,
														})
													}
												/>
											</div>
										)}
									</Card>
								);
							})}
						</div>
					)}
				</CardContent>
			</Card>

			<Card className="gap-2 p-4 shadow-none">
				<CardHeader className="p-0">
					<CardTitle className="flex items-center gap-2 text-lg">
						{t("table.rowsTitle")}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-3 p-0">
					<p className="text-muted-foreground text-xs">
						{fixedRows.length > 0
							? t("table.fixedRowsHint")
							: t("table.dynamicRowsHint", { n: TABLE_MAX_ROWS_LIMIT })}
					</p>
					{fixedRows.length > 0 && (
						<div className="flex flex-col gap-2">
							{fixedRows.map((row, idx) => (
								<div className="flex items-center gap-2" key={row.id}>
									<div className="flex shrink-0 items-center">
										<Button
											aria-label={t("table.moveRowUp", {
												name: row.label || String(idx + 1),
											})}
											className="size-7"
											disabled={idx === 0}
											onClick={() => moveFixedRow(idx, -1)}
											size="icon"
											type="button"
											variant="ghost"
										>
											<ChevronUp aria-hidden="true" className="size-3.5" />
										</Button>
										<Button
											aria-label={t("table.moveRowDown", {
												name: row.label || String(idx + 1),
											})}
											className="size-7"
											disabled={idx === fixedRows.length - 1}
											onClick={() => moveFixedRow(idx, 1)}
											size="icon"
											type="button"
											variant="ghost"
										>
											<ChevronDown aria-hidden="true" className="size-3.5" />
										</Button>
									</div>
									<Input
										aria-label={t("table.rowLabel")}
										onChange={(e) =>
											updateFixedRow(row.id, { label: e.target.value })
										}
										placeholder={t("table.rowLabelPlaceholder")}
										value={row.label}
									/>
									<Button
										aria-label={t("table.removeRow", {
											name: row.label || String(idx + 1),
										})}
										className="size-7 text-muted-foreground hover:text-destructive"
										onClick={() => removeFixedRow(row.id)}
										size="icon"
										type="button"
										variant="ghost"
									>
										<X aria-hidden="true" className="size-3.5" />
									</Button>
								</div>
							))}
						</div>
					)}
					<Button
						className="self-start"
						onClick={addFixedRow}
						size="sm"
						type="button"
						variant="outline"
					>
						<Plus aria-hidden="true" className="mr-1 size-3.5" />
						{t("table.addRow")}
					</Button>
					{fixedRows.length === 0 && (
						<div className="flex flex-col gap-2">
							<Label className="font-medium text-sm" htmlFor="table-max-rows">
								{t("table.maxRows")}
							</Label>
							<Input
								id="table-max-rows"
								inputMode="numeric"
								max={TABLE_MAX_ROWS_LIMIT}
								min={1}
								onChange={(e) => {
									const n = Number.parseInt(e.target.value, 10);
									onUpdateSettings({
										tableMaxRows: Number.isFinite(n)
											? Math.min(TABLE_MAX_ROWS_LIMIT, Math.max(1, n))
											: 10,
									});
								}}
								type="number"
								value={maxRows}
							/>
							<p className="text-muted-foreground text-xs">
								{t("table.maxRowsHelp", { n: TABLE_MAX_ROWS_LIMIT })}
							</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
